/**
 * One condition of a gate: the field it reads, how it tests it, and the value
 * it compares with, which the valueless operators do not take. The operators
 * offered are those of the field's type (Feature 73, 4.2); a stored operator
 * the type does not offer stays, flagged, until it is changed (D11), and
 * another field keeps the operator when its type offers it (D10). A gate's
 * own condition on a list also offers the three list tests (Feature 77 D27);
 * an item condition inside one does not (D22).
 */
import type { LinkageListTest, LinkageOperator, LinkageRule } from '@optum/api-client/types';

import { IconButton, Inline, Typography } from '../../ui/finixt.js';
import { Select } from '../../ui/Select.js';
import { itemPick, itemTest } from './item-picks.js';
import { isListOperator, toListTest } from './lists.js';
import {
  LENGTHS,
  VALUELESS,
  conditionOptions,
  isLength,
  operatorOn,
  operatorOptions,
} from './operators.js';
import {
  PICKER_WIDTH,
  notOfferedWords,
  pickerOptions,
  type ReferenceOption,
} from './references.js';
import { hasPosition, storedOption } from './positions.js';
import { PositionHint } from './PositionHint.js';
import { ValueInput } from './ValueInput.js';

/** The rule with another operator: a value is dropped or started as the operator needs. */
export function withOperator(rule: LinkageRule, op: LinkageOperator): LinkageRule {
  if (VALUELESS.has(op)) return { ref: rule.ref, op };
  return { ref: rule.ref, op, value: rule.value ?? '' };
}

/** The rule reading another field, its operator kept or changed as that field's type needs. */
export function withReference(
  rule: LinkageRule,
  ref: string,
  references: readonly ReferenceOption[],
): LinkageRule {
  const type = references.find((r) => r.value === ref)?.type;
  return withOperator({ ...rule, ref }, operatorOn(type, rule.op));
}

export function RuleRow({
  index,
  rule,
  references,
  onChange,
  onRemove,
  onList,
  label = `Condition ${index + 1}`,
  missing,
}: {
  index: number;
  rule: LinkageRule;
  references: readonly ReferenceOption[];
  onChange: (rule: LinkageRule) => void;
  onRemove: () => void;
  /** Given on a gate's own condition: choosing a list test turns the rule into one. */
  onList?: ((test: LinkageListTest) => void) | undefined;
  /** What the row's controls are named, `Condition 2` or `Condition 2 item 1`. */
  label?: string;
  /** What a reference the picker no longer offers is marked with. */
  missing?: string;
}) {
  const field = references.find((r) => r.value === rule.ref);
  const isLengthTest = LENGTHS.includes(rule.op);
  const chooseOperator = (op: string) => {
    if (onList && isListOperator(op)) onList(toListTest(rule.ref, op));
    else onChange(withOperator(rule, op as LinkageOperator));
  };
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        padding: 9,
        // A stored position is outlined until it is replaced (Feature 82 D6).
        border: `1px solid ${hasPosition(rule.ref) ? 'var(--danger)' : 'var(--border-hairline)'}`,
        borderRadius: 'var(--radius-control)',
        background: 'var(--surface-sunken)',
      }}
    >
      <Inline gap={5} align="center" wrap={false}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Select
            searchable
            aria-label={`${label} reference`}
            value={rule.ref}
            onChange={(ref) => {
              // A list's item field becomes a test on its list, where the row may hold one (F82 D9).
              const pick = itemPick(references, ref);
              if (pick && onList) onList(itemTest(pick));
              else onChange(withReference(rule, ref, references));
            }}
            panelWidth={PICKER_WIDTH}
            searchPlaceholder="Search a field…"
            options={[
              ...(field
                ? []
                : [storedOption(rule.ref, missing ?? notOfferedWords(references, rule.ref))]),
              ...pickerOptions(references),
            ]}
          />
        </div>
        <IconButton
          icon="close"
          size={28}
          tone="bare"
          aria-label={`Remove ${label.charAt(0).toLowerCase()}${label.slice(1)}`}
          onClick={onRemove}
        />
      </Inline>
      <PositionHint path={rule.ref} />
      <Select
        aria-label={`${label} operator`}
        value={rule.op}
        onChange={chooseOperator}
        options={
          onList ? conditionOptions(field?.type, rule.op) : operatorOptions(field?.type, rule.op)
        }
      />
      {!VALUELESS.has(rule.op) && (
        <ValueInput
          label={`${label} value`}
          value={rule.value}
          onChange={(value) => onChange({ ...rule, value })}
        />
      )}
      {isLengthTest && !isLength(rule.value) && (
        <Typography variant="meta" style={{ color: 'var(--warn)' }}>
          A length is a whole number from 0.
        </Typography>
      )}
    </div>
  );
}
